import { Typography } from "@mui/material"
import { formatDuration, intervalToDuration } from "date-fns"
import { ru } from "date-fns/locale"
import { TextFieldElement, useWatch } from "react-hook-form-mui"

const DEFAULT_SPEED = 50

interface IProps {
  required?: boolean
  /** Количество получателей для оценки общего времени рассылки. */
  recipientCount?: number
}
interface IField {
  speed?: number
}

/** Округление до десятой для человекочитаемого вывода. */
const round1 = (n: number): number => Math.round(n * 10) / 10

/**
 * Подсказка под полем скорости: интервал между сообщениями и оценка общего
 * времени рассылки. Показывается только когда введена корректная скорость.
 */
const SpeedHint = ({ recipientCount }: { recipientCount?: number }) => {
  const raw = useWatch<IField>({ name: "speed" })
  const speed = typeof raw === "number" ? raw : Number(raw)

  if (!speed || !Number.isFinite(speed) || speed <= 0) {
    return (
      <Typography variant="caption">
        По умолчанию {DEFAULT_SPEED} сообщений/сек.
      </Typography>
    )
  }

  const perMessageSec = round1(1 / speed)

  let total: string | null = null
  if (recipientCount && recipientCount > 0) {
    const totalSeconds = Math.round(recipientCount / speed)
    const duration = intervalToDuration({
      start: 0,
      end: totalSeconds * 1000,
    })
    total = formatDuration(duration, {
      locale: ru,
      format: ["hours", "minutes", "seconds"],
      zero: false,
    })
  }

  return (
    <Typography variant="caption" component="span">
      ≈ 1 сообщение каждые {perMessageSec} с.
      {total && (
        <>
          <br />
          Рассылка {recipientCount} получателям займёт ≈ {total}.
        </>
      )}
    </Typography>
  )
}

export const Speed = <T extends IField>({
  required,
  recipientCount,
}: IProps) => {
  return (
    <TextFieldElement<IField>
      name="speed"
      label="Скорость рассылки (сообщений/сек)"
      type="number"
      required={required}
      inputProps={{ step: 0.1, min: 0.1 }}
      helperText={<SpeedHint recipientCount={recipientCount} />}
    />
  )
}
